import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchResolutionSettings, fetchStore } from "../api/backend.js";
import { isPreviewMode, previewFixtures } from "../preview/fixtures.js";
import { loadFlow, saveFlow } from "../utils/session.js";

export const OccurrenceContext = createContext();

export function OccurrenceProvider({ children }) {
  const url = new URL(window.location.href);
  const shop = url.pathname.substring(1).split("/")[0];
  const previewEnabled = isPreviewMode();
  const cached = previewEnabled ? previewFixtures() : loadFlow();
  const [occurrence, setOccurrenceState] = useState(cached.occurrence);
  const [order, setOrderState] = useState(cached.order);
  const [store, setStore] = useState(previewEnabled ? cached.store : undefined);
  const [storeStatus, setStoreStatus] = useState(previewEnabled ? "ready" : "loading");
  const [resolutionSettings, setResolutionSettings] = useState(cached.resolutionSettings);
  const [resolutionType, setResolutionTypeState] = useState(cached.resolutionType);
  const [customerEmail, setCustomerEmailState] = useState(cached.customerEmail || "");
  const [result, setResultState] = useState(cached.result);

  useEffect(() => {
    if (previewEnabled) {
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setStoreStatus("loading");
      try {
        const [nextStore, nextSettings] = await Promise.all([
          fetchStore(shop),
          fetchResolutionSettings(shop).catch(() => undefined),
        ]);
        if (cancelled) {
          return;
        }
        setStore(nextStore);
        if (nextSettings) {
          setResolutionSettings(nextSettings);
          saveFlow({ resolutionSettings: nextSettings });
        }
        setStoreStatus("ready");
      } catch {
        if (!cancelled) {
          setStoreStatus("error");
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [previewEnabled, shop]);

  const setOrder = (next) => {
    setOrderState(next);
    saveFlow({ order: next });
  };

  const setOccurrence = (next) => {
    setOccurrenceState(next);
    saveFlow({ occurrence: next });
  };

  const persistResolutionSettings = (next) => {
    setResolutionSettings(next);
    saveFlow({ resolutionSettings: next });
  };

  const setResolutionType = (next) => {
    setResolutionTypeState(next);
    saveFlow({ resolutionType: next });
  };

  const setCustomerEmail = (next) => {
    setCustomerEmailState(next);
    saveFlow({ customerEmail: next });
  };

  const setResult = (next) => {
    setResultState(next);
    saveFlow({ result: next });
  };

  const value = useMemo(() => ({
    shop,
    store,
    storeStatus,
    setStore,
    order,
    setOrder,
    occurrence,
    setOccurrence,
    resolutionSettings,
    setResolutionSettings: persistResolutionSettings,
    resolutionType,
    setResolutionType,
    customerEmail,
    setCustomerEmail,
    result,
    setResult,
  }), [
    shop,
    store,
    storeStatus,
    order,
    occurrence,
    resolutionSettings,
    resolutionType,
    customerEmail,
    result,
  ]);

  return (
    <OccurrenceContext.Provider value={value}>
      {children}
    </OccurrenceContext.Provider>
  );
}

export function useOccurrence() {
  return useContext(OccurrenceContext);
}
